/**
 * useUndoManualPayment - Hook para cancelar (estornar) uma baixa MANUAL de cobrança
 *
 * Fluxo (fonte única RPC + q_charge), espelho do useMarkChargeAsPaidManual:
 * 1. Chama a RPC `undo_manual_payment` (única porta).
 *    A RPC:
 *      - Valida tenant + permissão granular `financial_charges.edit`
 *      - Aceita SOMENTE parcelas com `paid_source = 'manual'` (baixas confirmadas
 *        pelo provedor via webhook não podem ser desfeitas pelo usuário)
 *      - Limpa os campos de pagamento e devolve o status em aberto
 *        (vencido / aguardando_pagamento / pendente), guardando o histórico
 *        em `metadata.manual_payment_reverted`
 *      - Registra `charge_manual_payment_reverted` na timeline
 *      - Quando a parcela está sincronizada, enfileira `undo_received_in_cash`
 *        em q_charge para o worker desfazer também no provedor (Asaas
 *        POST /v3/payments/{id}/undoReceivedInCash)
 * 2. Invalida caches.
 *
 * @author Módulo Financeiro
 * RELEASE: 2026-08-19 v1.0.0 - Criação do hook
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useUndoManualPayment');

export interface UndoManualPaymentPayload {
  /** Parcela cuja baixa manual será revertida */
  parcela_id: string;
  /** Cobrança-pai (usada apenas para invalidar o cache do modal de detalhes) */
  cobranca_id: string;
  /** Motivo do estorno (opcional, gravado na timeline e no metadata) */
  reason?: string | null;
}

export function useUndoManualPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UndoManualPaymentPayload) => {
      logger.debug('Cancelando recebimento em dinheiro via RPC única', {
        parcela_id: payload.parcela_id,
      });

      const { data, error } = await supabase.rpc('undo_manual_payment', {
        p_parcela_id: payload.parcela_id,
        p_reason: payload.reason?.trim() || null,
      });

      if (error) {
        logger.error('Erro ao chamar RPC undo_manual_payment', { error: error.message });
        throw error;
      }

      return data as unknown as {
        success: boolean;
        parcela_status: string;
        cobranca_status: string | null;
        sync_enqueued?: boolean;
      };
    },
    onSuccess: (data, variables) => {
      if (data?.sync_enqueued) {
        toast.success('Recebimento cancelado. Sincronização com o provedor em andamento.');
      } else {
        toast.success('Recebimento cancelado com sucesso');
      }

      queryClient.invalidateQueries({ queryKey: ['cobranca-details', variables.cobranca_id] });
      queryClient.invalidateQueries({ queryKey: ['cobranca-details'] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas'] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-parcelas'] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-parcelas-paginated'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao cancelar recebimento');
    },
  });
}
